import classNames from 'classnames'
import { selectJBPSettings } from 'legacy/lut/features/partner360/jbpSettings'
import { FC, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { Translation } from 'cng-common/features/translations'
import { TOUR_TARGETS } from 'partner360/configs/widgetTour/planningActivitiesTourTargets'
import { TKeyInitiative } from 'partner360/entities/keyInitiatives'
import { TRANSLATION_KEYS } from 'partner360/features/keyInitiatives/configs/translations'
import { DashboardChartMeasure } from 'partner360/features/keyInitiatives/model/types'
import {
  getGoalField,
  getInitiativeIncrementalField,
} from 'partner360/features/keyInitiatives/ui/helpers/fieldHelper'
import { formatMoney } from 'partner360/features/keyInitiatives/ui/helpers/formatMoney'
import { ActualsValueTypes } from 'partner360/models/actualValueTypes'

import { getBusinessPriorityNames } from '../../../../helpers/getBusinessPriorityNames'
import { getNumberValue } from '../../../../helpers/getNumberValue'
import { useKeyInitiativesControlsForm } from '../../../../hooks/useKeyInitiativesControlsForm'
import { DashboardLabel } from '../../common/dashboardLabel/DashboardLabel'

import styles from './StrategicInitiativeBusinessPriorityHeatMap.module.scss'

interface IProps {
  data: TKeyInitiative[]
  className?: string
  activeCurrency: string
  actualsType: ActualsValueTypes
}

interface ICellData {
  value: number
  count: number
}

const EMPTY_VALUE = '·'

const getCellClassName = (value: number, maxValue: number) => {
  if (!value || !maxValue) {
    return styles.emptyCell
  }

  const percent = value / maxValue

  if (percent >= 0.66) {
    return styles.highCell
  }

  if (percent >= 0.33) {
    return styles.mediumCell
  }

  return styles.lowCell
}

export const StrategicInitiativeBusinessPriorityHeatMap: FC<IProps> = ({
  data,
  className,
  actualsType,
  activeCurrency,
}) => {
  const { chartMeasure } = useKeyInitiativesControlsForm()
  const { kiBusinessPriorities } = useSelector(selectJBPSettings)

  const {
    strategicInitiatives,
    businessPriorities,
    matrix,
    bpCountBySI,
    siCountByBP,
    maxCellValue,
    totalDistinctPairs,
  } = useMemo(() => {
    const strategicInitiativesSet = new Set<string>()
    const businessPrioritiesSet = new Set<string>()

    const matrixMap = new Map<string, Map<string, ICellData>>()
    const bpBySI = new Map<string, Set<string>>()
    const siByBP = new Map<string, Set<string>>()
    const distinctPairs = new Set<string>()

    const measure =
      chartMeasure === DashboardChartMeasure.Count ? DashboardChartMeasure.Count : chartMeasure

    const measureField =
      measure === DashboardChartMeasure.Goal
        ? getGoalField(actualsType, activeCurrency)
        : getInitiativeIncrementalField(actualsType, activeCurrency)

    data.forEach((item) => {
      const strategicInitiative = item.Development
      const businessPriorities = getBusinessPriorityNames(
        kiBusinessPriorities,
        item.BusinessPriorities
      )

      if (!strategicInitiative || !businessPriorities.length) {
        return
      }

      strategicInitiativesSet.add(strategicInitiative)

      const rawValue =
        measure === DashboardChartMeasure.Count ? 1 : getNumberValue(item, measureField)

      const valuePerPriority = rawValue / businessPriorities.length

      businessPriorities.forEach((businessPriority) => {
        businessPrioritiesSet.add(businessPriority)

        if (!matrixMap.has(strategicInitiative)) {
          matrixMap.set(strategicInitiative, new Map())
        }

        const row = matrixMap.get(strategicInitiative)
        const currentCell = row?.get(businessPriority) || { value: 0, count: 0 }

        row?.set(businessPriority, {
          value: currentCell.value + valuePerPriority,
          count: currentCell.count + 1,
        })

        if (!bpBySI.has(strategicInitiative)) {
          bpBySI.set(strategicInitiative, new Set())
        }

        if (!siByBP.has(businessPriority)) {
          siByBP.set(businessPriority, new Set())
        }

        bpBySI.get(strategicInitiative)?.add(businessPriority)
        siByBP.get(businessPriority)?.add(strategicInitiative)
        distinctPairs.add(`${strategicInitiative}__${businessPriority}`)
      })
    })

    const rows = Array.from(strategicInitiativesSet)
    const columns = Array.from(businessPrioritiesSet)

    const maxValue = rows.reduce((max, row) => {
      const rowMap = matrixMap.get(row)

      const rowMax = columns.reduce((currentMax, column) => {
        const value = rowMap?.get(column)?.value || 0

        return Math.max(currentMax, value)
      }, 0)

      return Math.max(max, rowMax)
    }, 0)

    return {
      strategicInitiatives: rows,
      businessPriorities: columns,
      matrix: matrixMap,
      bpCountBySI: bpBySI,
      siCountByBP: siByBP,
      maxCellValue: maxValue,
      totalDistinctPairs: distinctPairs.size,
    }
  }, [chartMeasure, actualsType, activeCurrency, data, kiBusinessPriorities])

  const renderCellValue = (cell?: ICellData) => {
    if (!cell) {
      return EMPTY_VALUE
    }

    if (chartMeasure === DashboardChartMeasure.Count) {
      return cell.count
    }

    return formatMoney(cell.value, activeCurrency)
  }

  if (strategicInitiatives.length === 0 || businessPriorities.length === 0) {
    return null
  }

  return (
    <div className={classNames(styles.root, className)} data-tour-id={TOUR_TARGETS.analysisHeatmap}>
      <div className={styles.header}>
        <DashboardLabel
          translationKey={TRANSLATION_KEYS.dashboard.charts.spBi.title}
          type={'title'}
        />

        <DashboardLabel
          translationKey={TRANSLATION_KEYS.dashboard.charts.spBi.subTitle}
          type={'subTitle'}
          replaceParams={[
            { key: '{chartMeasure}', value: chartMeasure },
            { key: '{activeCurrency}', value: activeCurrency },
            { key: '{actualsType}', value: actualsType },
          ]}
        />
      </div>

      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={classNames(styles.stickyLeft, styles.cornerCell)}>
                <Translation span text={TRANSLATION_KEYS.dashboard.charts.spBi.siBp} />
              </th>

              {businessPriorities.map((businessPriority) => (
                <th key={businessPriority}>{businessPriority}</th>
              ))}

              <th className={classNames(styles.stickyRight, styles.marginHeader)}>
                <Translation span text={TRANSLATION_KEYS.dashboard.charts.spBi.bpPerSi} />
              </th>
            </tr>
          </thead>

          <tbody>
            {strategicInitiatives.map((strategicInitiative) => {
              const row = matrix.get(strategicInitiative)

              return (
                <tr key={strategicInitiative}>
                  <th className={styles.stickyLeft}>{strategicInitiative}</th>

                  {businessPriorities.map((businessPriority) => {
                    const cell = row?.get(businessPriority)

                    return (
                      <td
                        key={businessPriority}
                        className={getCellClassName(cell?.value || 0, maxCellValue)}
                      >
                        {renderCellValue(cell)}
                      </td>
                    )
                  })}

                  <td className={classNames(styles.stickyRight, styles.marginCell)}>
                    {bpCountBySI.get(strategicInitiative)?.size || 0}
                  </td>
                </tr>
              )
            })}

            <tr className={styles.footerRow}>
              <th className={styles.stickyLeft}>
                <Translation span text={TRANSLATION_KEYS.dashboard.charts.spBi.siPerBp} />
              </th>

              {businessPriorities.map((businessPriority) => (
                <td key={businessPriority}>{siCountByBP.get(businessPriority)?.size || 0}</td>
              ))}

              <td className={classNames(styles.stickyRight, styles.marginCell)}>
                {totalDistinctPairs}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className={styles.legend}>
        <Translation span text={TRANSLATION_KEYS.dashboard.charts.spBi.text1} />
        <span className={classNames(styles.legendItem, styles.lowCell)} />
        <span className={classNames(styles.legendItem, styles.mediumCell)} />
        <span className={classNames(styles.legendItem, styles.highCell)} />
        <Translation text={TRANSLATION_KEYS.dashboard.charts.spBi.text2} />
      </div>
    </div>
  )
}
